<script setup>
import { v4 as uuid } from 'uuid'
import { ref, watch, onMounted, useAttrs } from 'vue'
import Icon from '@/Shared/Icon.vue'

const props = defineProps({
  id: {
    type: String,
    default() {
      return `status-filter-${uuid()}`
    }
  },
  error: {
    type: String,
    default: null
  },
  label: {
    type: String,
    default: null
  },
  options: {
    type: Array,
    required: true
  },
  modelValue: {
    type: String,
    default: null
  },
  statusColors: {
    type: Object,
    default: () => ({})
  },
  colorMap: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:modelValue'])

const selected = ref(props.modelValue)
const isOpen = ref(false)

watch(
  () => props.modelValue,
  (newVal) => {
    selected.value = newVal
  }
)

watch(selected, (value) => {
  emit('update:modelValue', value)
})

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
}

const selectOption = (value) => {
  selected.value = value
  isOpen.value = false
}

const closeDropdown = () => {
  isOpen.value = false
}

const getSelectedOption = () => {
  return props.options.find((option) => option.value === selected.value)
}

const getStatusColor = (value) => {
  const color = props.statusColors[value]
  // Si el color ya es una clase de Tailwind, devolverla directamente
  if (color && color.startsWith('bg-')) {
    return color
  }
  // Si es un color CSS estándar, aplicarlo como estilo en lugar de clase
  return null
}

const getStatusStyle = (value) => {
  const color = props.statusColors[value]
  // Si el color no es una clase de Tailwind, aplicarlo como estilo
  if (color && !color.startsWith('bg-')) {
    return { backgroundColor: color }
  }
  return {}
}

onMounted(() => {
  document.addEventListener('click', (e) => {
    if (!e.target.closest(`#${props.id}-container`)) {
      closeDropdown()
    }
  })
})

const attrs = useAttrs()
</script>

<template>
  <div :id="`${id}-container`" :class="attrs.class || 'mb-6'">
    <label v-if="label" class="form-label" :for="id">{{ label }}:</label>
    <div class="relative">
      <button
        :id="id"
        type="button"
        class="grid w-full grid-cols-1 rounded-md bg-white py-2 px-3 text-left text-gray-700 border border-gray-300 focus:border-indigo-400 focus:outline-none focus:ring"
        :class="{ 'border-red-500 focus:ring-red-200': error }"
        aria-haspopup="listbox"
        :aria-expanded="isOpen"
        @click="toggleDropdown"
      >
        <span class="col-start-1 row-start-1 flex items-center gap-2 pr-6">
          <span
            v-if="getSelectedOption()"
            class="h-3 w-3 rounded-full"
            :class="getStatusColor(selected)"
            :style="getStatusStyle(selected)"
          ></span>
          <span class="block truncate">{{ getSelectedOption()?.name || 'Seleccionar' }}</span>
        </span>
        <Icon
          name="ChevronUpDown"
          class="col-start-1 row-start-1 h-5 w-5 self-center justify-self-end text-gray-400"
        />
      </button>

      <transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95"
      >
        <ul
          v-if="isOpen"
          class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm"
          tabindex="-1"
          role="listbox"
        >
          <li
            v-for="option in options"
            :key="option.value"
            role="option"
            class="relative cursor-pointer select-none py-2 pl-3 pr-9"
            :class="{
              'bg-indigo-600 text-white': selected === option.value,
              'text-gray-900 hover:bg-gray-100': selected !== option.value
            }"
            @click="selectOption(option.value)"
          >
            <div class="flex items-center">
              <span
                class="h-3 w-3 rounded-full mr-2"
                :class="{
                  'ring-2 ring-white': selected === option.value,
                  [getStatusColor(option.value)]: getStatusColor(option.value)
                }"
                :style="getStatusStyle(option.value)"
              ></span>
              <span
                class="block truncate"
                :class="{
                  'font-medium': selected === option.value,
                  'font-normal': selected !== option.value
                }"
              >
                {{ option.name }}
              </span>
            </div>

            <span
              v-if="selected === option.value"
              class="absolute inset-y-0 right-0 flex items-center pr-4"
              :class="{
                'text-white': selected === option.value,
                'text-indigo-600': selected !== option.value
              }"
            >
              <Icon name="Check" class="h-5 w-5" />
            </span>
          </li>
        </ul>
      </transition>
    </div>
    <div v-if="error" class="form-error">{{ error }}</div>
  </div>
</template>
